/**
 * Spaces API Documentation Page
 *
 * Comprehensive documentation for the Losi Spaces API with interactive examples
 * and testing capabilities.
 */

"use client"

import { useState } from "react"
import { IconCode, IconPlay, IconDownload, IconBook } from "@tabler/icons-react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Badge } from "@/components/ui/badge"
import { Separator } from "@/components/ui/separator"
import { useTranslation } from "react-i18next"
import ApiPageLayout from "@/components/layout/ApiPageLayout"

export default function SpacesApiPage() {
  const { t } = useTranslation()
  const [activeTab, setActiveTab] = useState("overview")

  const spaceExample = {
    id: "space_123456789",
    user_id: "user_987654321",
    workspace_id: "workspace_abc123",
    name: "Product Launch 2025",
    description: "Project management space for the Q1 product launch",
    icon: "🚀",
    color: "#3B82F6",
    default_view: "board",
    sharing: "private",
    created_at: "2025-01-01T00:00:00Z",
    updated_at: "2025-01-01T00:00:00Z",
    status_template: {
      todo: { name: "To Do", color: "#6B7280", position: 1 },
      in_progress: { name: "In Progress", color: "#3B82F6", position: 2 },
      done: { name: "Done", color: "#10B981", position: 3 }
    },
    priority_template: {
      low: { name: "Low", color: "#6B7280", position: 1 },
      medium: { name: "Medium", color: "#F59E0B", position: 2 },
      high: { name: "High", color: "#EF4444", position: 3 }
    }
  }

  const taskExample = {
    id: "task_123456789",
    user_id: "user_987654321",
    space_id: "space_123456789",
    title: "Implement user authentication system",
    description: "Build secure login and registration with email verification",
    status: "in_progress",
    priority: "high",
    assigned_to: "user_987654321",
    due_date: "2025-02-15",
    tags: ["backend", "security", "authentication"],
    position: 1,
    created_at: "2025-01-01T00:00:00Z",
    updated_at: "2025-01-01T00:00:00Z",
    assignee: {
      id: "user_987654321",
      username: "john.doe",
      image_url: "https://example.com/avatar.jpg"
    }
  }

  const noteExample = {
    id: "note_123456789",
    user_id: "user_987654321",
    space_id: "space_123456789",
    title: "Technical Architecture Overview",
    content: "# System Architecture\n\n## Components\n- Frontend: Next.js with React\n- Backend: Supabase with PostgreSQL\n- API: RESTful with OpenAPI spec\n\n## Security\n- JWT authentication\n- Row Level Security (RLS)\n- API key management",
    content_type: "markdown",
    sharing: "space",
    created_at: "2025-01-01T00:00:00Z",
    updated_at: "2025-01-01T00:00:00Z",
    author: {
      id: "user_987654321",
      username: "john.doe",
      image_url: "https://example.com/avatar.jpg"
    }
  }

  const apiExamples = [
    {
      title: "List Spaces",
      method: "GET",
      endpoint: "/api/v1/spaces?workspace_id=workspace_123",
      description: "Get all spaces in a workspace",
      code: `curl -H 'Authorization: Bearer losi-abc123...' \\
  'https://your-domain.com/api/v1/spaces?workspace_id=workspace_123'`
    },
    {
      title: "Create Space",
      method: "POST",
      endpoint: "/api/v1/spaces",
      description: "Create a new project management space",
      code: `curl -X POST -H 'Authorization: Bearer losi-abc123...' \\
  -H 'Content-Type: application/json' \\
  -d '{
    "workspace_id": "workspace_123",
    "name": "Q1 Product Launch",
    "description": "Project management for product launch",
    "icon": "🚀",
    "default_view": "board"
  }' \\
  https://your-domain.com/api/v1/spaces`
    },
    {
      title: "Get Space Details",
      method: "GET",
      endpoint: "/api/v1/spaces/{space_id}",
      description: "Get detailed information about a specific space",
      code: `curl -H 'Authorization: Bearer losi-abc123...' \\
  https://your-domain.com/api/v1/spaces/space_123`
    },
    {
      title: "Create Task",
      method: "POST",
      endpoint: "/api/v1/spaces/{space_id}/tasks",
      description: "Add a new task to a space",
      code: `curl -X POST -H 'Authorization: Bearer losi-abc123...' \\
  -H 'Content-Type: application/json' \\
  -d '{
    "title": "Implement user authentication",
    "description": "Build secure login system",
    "status": "todo",
    "priority": "high",
    "assigned_to": "user_456",
    "due_date": "2025-02-15"
  }' \\
  https://your-domain.com/api/v1/spaces/space_123/tasks`
    },
    {
      title: "List Tasks",
      method: "GET",
      endpoint: "/api/v1/spaces/{space_id}/tasks",
      description: "Get all tasks in a space with optional filtering",
      code: `curl -H 'Authorization: Bearer losi-abc123...' \\
  'https://your-domain.com/api/v1/spaces/space_123/tasks?status=in_progress&priority=high'`
    },
    {
      title: "Create Note",
      method: "POST",
      endpoint: "/api/v1/spaces/{space_id}/notes",
      description: "Add documentation or meeting notes to a space",
      code: `curl -X POST -H 'Authorization: Bearer losi-abc123...' \\
  -H 'Content-Type: application/json' \\
  -d '{
    "title": "Technical Architecture",
    "content": "# System Overview\\n\\n## Components\\n- Frontend: Next.js\\n- Backend: Supabase",
    "content_type": "markdown"
  }' \\
  https://your-domain.com/api/v1/spaces/space_123/notes`
    }
  ]

  const downloadPostmanCollection = () => {
    const collection = {
      info: {
        name: "Losi Spaces API",
        description: "Complete API collection for Losi Spaces management",
        schema: "https://schema.getpostman.com/json/collection/v2.1.0/collection.json"
      },
      item: [
        {
          name: "Spaces",
          item: [
            {
              name: "List Spaces",
              request: {
                method: "GET",
                header: [
                  { key: "Authorization", value: "Bearer losi-{{api_key}}" }
                ],
                url: {
                  raw: "{{base_url}}/api/v1/spaces?workspace_id={{workspace_id}}",
                  host: ["{{base_url}}"],
                  path: ["api", "v1", "spaces"],
                  query: [{ key: "workspace_id", value: "{{workspace_id}}" }]
                }
              }
            },
            {
              name: "Create Space",
              request: {
                method: "POST",
                header: [
                  { key: "Authorization", value: "Bearer losi-{{api_key}}" },
                  { key: "Content-Type", value: "application/json" }
                ],
                body: {
                  mode: "raw",
                  raw: JSON.stringify({
                    workspace_id: "{{workspace_id}}",
                    name: "New Project Space",
                    description: "Project management space",
                    icon: "📁",
                    default_view: "board"
                  }, null, 2)
                },
                url: {
                  raw: "{{base_url}}/api/v1/spaces",
                  host: ["{{base_url}}"],
                  path: ["api", "v1", "spaces"]
                }
              }
            }
          ]
        }
      ],
      variable: [
        { key: "base_url", value: "https://your-domain.com" },
        { key: "api_key", value: "your-api-key-here" },
        { key: "workspace_id", value: "your-workspace-id" }
      ]
    }

    const blob = new Blob([JSON.stringify(collection, null, 2)], {
      type: "application/json"
    })
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = "losi-spaces-api.postman_collection.json"
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  return (
    <ApiPageLayout
      title="Spaces API Documentation"
      actions={
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={downloadPostmanCollection}
            className="hover:bg-secondary"
          >
            <IconDownload size={16} className="mr-2" />
            Download Postman Collection
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.open('/docs/api-v1-usage.md#9-apiv1spaces-spaces-management-api', '_blank')}
            className="hover:bg-secondary"
          >
            <IconBook size={16} className="mr-2" />
            Full API Reference
          </Button>
        </div>
      }
    >
      <div className="bg-card mb-6 rounded-lg border p-6 shadow-sm">
        <div className="mb-4 flex items-center gap-3">
          <div className="bg-primary/10 rounded-lg p-2">
            <IconCode size={24} className="text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-semibold">Spaces API</h2>
            <p className="text-muted-foreground">
              Programmatically manage Losi Spaces, tasks, notes, and team collaboration
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className="bg-muted/50 rounded-lg p-4">
            <div className="mb-2 flex items-center gap-2">
              <Badge variant="secondary">REST API</Badge>
              <Badge variant="outline">JSON</Badge>
            </div>
            <p className="text-muted-foreground text-sm">
              Standard RESTful endpoints with JSON request/response format
            </p>
          </div>

          <div className="bg-muted/50 rounded-lg p-4">
            <div className="mb-2 flex items-center gap-2">
              <Badge variant="secondary">API Key Auth</Badge>
              <Badge variant="outline">Bearer Token</Badge>
            </div>
            <p className="text-muted-foreground text-sm">
              Secure authentication using API keys with spaces permission
            </p>
          </div>

          <div className="bg-muted/50 rounded-lg p-4">
            <div className="mb-2 flex items-center gap-2">
              <Badge variant="secondary">Rate Limited</Badge>
              <Badge variant="outline">1000 req/hr</Badge>
            </div>
            <p className="text-muted-foreground text-sm">
              Reasonable rate limits to ensure platform stability
            </p>
          </div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="examples">Examples</TabsTrigger>
          <TabsTrigger value="schemas">Data Schemas</TabsTrigger>
          <TabsTrigger value="testing">Testing</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>API Capabilities</CardTitle>
              <CardDescription>
                Complete programmatic access to Losi Spaces functionality
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <h4 className="mb-2 font-medium">Space Management</h4>
                  <ul className="text-muted-foreground space-y-1 text-sm">
                    <li>• Create and manage project spaces</li>
                    <li>• Configure custom status and priority templates</li>
                    <li>• Set sharing and visibility options</li>
                    <li>• Archive and restore spaces</li>
                  </ul>
                </div>

                <div>
                  <h4 className="mb-2 font-medium">Task Management</h4>
                  <ul className="text-muted-foreground space-y-1 text-sm">
                    <li>• Create, update, and track tasks</li>
                    <li>• Assign tasks to team members</li>
                    <li>• Set priorities, due dates, and tags</li>
                    <li>• Filter tasks by status, priority, assignee</li>
                  </ul>
                </div>

                <div>
                  <h4 className="mb-2 font-medium">Note Management</h4>
                  <ul className="text-muted-foreground space-y-1 text-sm">
                    <li>• Create rich markdown documentation</li>
                    <li>• Organize project knowledge</li>
                    <li>• Share notes with team members</li>
                    <li>• Track note authors and versions</li>
                  </ul>
                </div>

                <div>
                  <h4 className="mb-2 font-medium">Team Collaboration</h4>
                  <ul className="text-muted-foreground space-y-1 text-sm">
                    <li>• Manage space memberships</li>
                    <li>• Assign roles (owner, admin, editor, viewer)</li>
                    <li>• Control access permissions</li>
                    <li>• Invite new team members</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Getting Started</CardTitle>
              <CardDescription>
                Follow these steps to start using the Spaces API
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ol className="list-inside list-decimal space-y-2 text-sm">
                <li><strong>Enable API Access:</strong> Go to your profile settings and enable API access</li>
                <li><strong>Create API Key:</strong> Generate a new API key with &quot;spaces&quot; permission</li>
                <li><strong>Get Workspace ID:</strong> Find your workspace ID from the URL or API</li>
                <li><strong>Make API Calls:</strong> Use Bearer token authentication for all requests</li>
                <li><strong>Test Endpoints:</strong> Start with listing spaces, then create your first space</li>
              </ol>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="examples" className="space-y-6">
          <div className="grid grid-cols-1 gap-6">
            {apiExamples.map((example, index) => (
              <Card key={index}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-lg">{example.title}</CardTitle>
                      <CardDescription>{example.description}</CardDescription>
                    </div>
                    <Badge variant={example.method === 'GET' ? 'secondary' : example.method === 'POST' ? 'default' : 'outline'}>
                      {example.method}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="bg-muted rounded-lg p-4">
                    <code className="whitespace-pre-wrap text-sm">{example.code}</code>
                  </div>
                  <div className="text-muted-foreground mt-3 text-xs">
                    <strong>Endpoint:</strong> <code>{example.endpoint}</code>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="schemas" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Space Object Schema</CardTitle>
              <CardDescription>
                Complete structure of a space object
              </CardDescription>
            </CardHeader>
            <CardContent>
              <pre className="bg-muted overflow-x-auto rounded-lg p-4 text-sm">
                <code>{JSON.stringify(spaceExample, null, 2)}</code>
              </pre>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Task Object Schema</CardTitle>
              <CardDescription>
                Structure of a task object with assignee information
              </CardDescription>
            </CardHeader>
            <CardContent>
              <pre className="bg-muted overflow-x-auto rounded-lg p-4 text-sm">
                <code>{JSON.stringify(taskExample, null, 2)}</code>
              </pre>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Note Object Schema</CardTitle>
              <CardDescription>
                Structure of a note object with author information
              </CardDescription>
            </CardHeader>
            <CardContent>
              <pre className="bg-muted overflow-x-auto rounded-lg p-4 text-sm">
                <code>{JSON.stringify(noteExample, null, 2)}</code>
              </pre>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="testing" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>API Testing Tools</CardTitle>
              <CardDescription>
                Test the Spaces API with these tools and examples
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <Button
                  variant="outline"
                  onClick={downloadPostmanCollection}
                  className="h-auto flex-col items-start p-4"
                >
                  <div className="mb-2 flex items-center gap-2">
                    <IconDownload size={20} />
                    <span className="font-medium">Postman Collection</span>
                  </div>
                  <p className="text-muted-foreground text-left text-sm">
                    Complete Postman collection with all endpoints and example requests
                  </p>
                </Button>

                <Button
                  variant="outline"
                  onClick={() => window.open('https://curlconverter.com/', '_blank')}
                  className="h-auto flex-col items-start p-4"
                >
                  <div className="mb-2 flex items-center gap-2">
                    <IconCode size={20} />
                    <span className="font-medium">cURL Converter</span>
                  </div>
                  <p className="text-muted-foreground text-left text-sm">
                    Convert between cURL commands and other formats
                  </p>
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Quick Test</CardTitle>
              <CardDescription>
                Test your API key and permissions
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="bg-muted rounded-lg p-4">
                <p className="mb-3 text-sm">Run this command to test your API key:</p>
                <code className="block whitespace-pre-wrap text-sm">
{`curl -H 'Authorization: Bearer losi-your-api-key-here' \\
  'https://your-domain.com/api/v1/spaces?workspace_id=your-workspace-id'`}
                </code>
                <p className="text-muted-foreground mt-3 text-sm">
                  This should return a JSON array of your spaces (or empty array if none exist).
                  If you get an authentication error, check your API key and permissions.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </ApiPageLayout>
  )
}
